import { getToolGroupLabel, type NavToolMenuItem } from '@/components/NavMenus/navMenuConfig';

export type ToolFinderGoal = 'explore' | 'enrich' | 'code' | 'visualize' | 'download' | 'ask_ai';

export type ToolFinderInterface = 'ui' | 'code' | 'either';

export type ToolFinderAnswers = {
  goal: ToolFinderGoal;
  interfacePref: ToolFinderInterface;
};

export type ToolFinderOption<T extends string> = {
  id: T;
  label: string;
  hint: string;
};

export const TOOL_FINDER_GOAL_OPTIONS: ToolFinderOption<ToolFinderGoal>[] = [
  { id: 'explore', label: 'Explore associations', hint: 'Search genes, diseases, variants' },
  { id: 'enrich', label: 'Enrich a gene set', hint: 'Functional / ranked enrichment' },
  { id: 'code', label: 'Use data in code', hint: 'API, R, or AI agents' },
  { id: 'visualize', label: 'Visualize networks', hint: 'Graphs and hierarchies' },
  { id: 'download', label: 'Download data', hint: 'Full database export' },
  { id: 'ask_ai', label: 'Ask in natural language', hint: 'Chat with AIDA' },
];

export const TOOL_FINDER_INTERFACE_OPTIONS: ToolFinderOption<ToolFinderInterface>[] = [
  { id: 'ui', label: 'Web interface', hint: 'Point-and-click tools' },
  { id: 'code', label: 'Code / scripts', hint: 'Programmatic access' },
  { id: 'either', label: 'Either is fine', hint: 'No preference' },
];

const GOAL_TOOL_WEIGHTS: Record<ToolFinderGoal, Partial<Record<string, number>>> = {
  explore: {
    'advanced-search': 5,
    ai: 4,
    'ontology-explorer': 3,
    'geo-browse': 2,
    enrichment: 1,
  },
  enrich: {
    enrichment: 5,
    'gsea-ranked': 5,
    'advanced-search': 2,
    ai: 1,
  },
  code: {
    api: 5,
    'r-package': 4,
    mcp: 4,
    download: 2,
    ai: 1,
  },
  visualize: {
    cytoscape: 5,
    'ontology-explorer': 4,
    'geo-browse': 3,
    enrichment: 2,
  },
  download: {
    download: 5,
    api: 3,
    'r-package': 2,
  },
  ask_ai: {
    ai: 5,
    mcp: 3,
    api: 2,
  },
};

const INTERFACE_BOOST: Record<ToolFinderInterface, Partial<Record<string, number>>> = {
  ui: {
    'advanced-search': 2,
    enrichment: 2,
    'gsea-ranked': 2,
    ai: 2,
    cytoscape: 1,
    'geo-browse': 1,
    'ontology-explorer': 1,
  },
  code: {
    api: 2,
    'r-package': 2,
    mcp: 2,
    download: 1,
  },
  either: {},
};

export type RankedTool = {
  tool: NavToolMenuItem;
  score: number;
};

export function rankToolsLocally(
  answers: ToolFinderAnswers,
  tools: NavToolMenuItem[],
  limit = 2,
): RankedTool[] {
  const goalWeights = GOAL_TOOL_WEIGHTS[answers.goal];
  const interfaceBoost = INTERFACE_BOOST[answers.interfacePref];

  return tools
    .map(tool => ({
      tool,
      score: (goalWeights[tool.id] ?? 0) + (interfaceBoost[tool.id] ?? 0),
    }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || a.tool.label.localeCompare(b.tool.label))
    .slice(0, limit);
}

export function buildLocalRationale(answers: ToolFinderAnswers, ranked: RankedTool[]): string {
  const goalLabel = TOOL_FINDER_GOAL_OPTIONS.find(o => o.id === answers.goal)?.label ?? answers.goal;
  const names = ranked.map(r => r.tool.label).join(' and ');
  if (!names) {
    return `Based on “${goalLabel}”, browse the catalog below or refine your answers.`;
  }
  return `Based on “${goalLabel}”, ${names} look like the best fit from the DISGENET tools available to you.`;
}

const TOOL_ALIASES: Record<string, string[]> = {
  ai: ['aida', 'ai assistant', 'disgenet ai'],
  api: ['rest api', 'api', 'interactive console'],
  'r-package': ['r package', 'disgenet2r', 'r integration'],
  mcp: ['mcp', 'mcp connector'],
  enrichment: ['enrichment', 'dsea', 'functional enrichment'],
  'gsea-ranked': ['gsea', 'ranked enrichment'],
  'advanced-search': ['advanced search', 'query builder'],
  'geo-browse': ['expressnet', 'geo browse', 'gene expression'],
  'ontology-explorer': ['ontology explorer', 'ontology graph'],
  cytoscape: ['cytoscape', 'cytoscape app'],
  download: ['download', 'database dump', 'full database'],
};

function resolveToolToken(token: string, tools: NavToolMenuItem[]): string | undefined {
  const cleaned = token.trim().replace(/^[[`"']+|[[`"'\]]+$/g, '');
  if (!cleaned) return undefined;

  const lower = cleaned.toLowerCase();
  const exact = tools.find(t => t.id.toLowerCase() === lower || t.label.toLowerCase() === lower);
  if (exact) return exact.id;

  for (const tool of tools) {
    const aliases = TOOL_ALIASES[tool.id] ?? [];
    if (aliases.some(alias => lower === alias || lower.includes(alias))) return tool.id;
  }

  return undefined;
}

function extractToolsFromFreeText(text: string, tools: NavToolMenuItem[]): string[] {
  const found: { id: string; index: number }[] = [];

  for (const tool of tools) {
    const candidates = [tool.id, tool.label, ...(TOOL_ALIASES[tool.id] ?? [])];
    for (const candidate of candidates) {
      const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\b${escaped}\\b`, 'i');
      const match = re.exec(text);
      if (match && match.index != null) {
        found.push({ id: tool.id, index: match.index });
        break;
      }
    }
  }

  return [...new Set(found.sort((a, b) => a.index - b.index).map(item => item.id))].slice(0, 2);
}

export function buildToolFinderPrompt(answers: ToolFinderAnswers, tools: NavToolMenuItem[]): string {
  const goalLabel = TOOL_FINDER_GOAL_OPTIONS.find(o => o.id === answers.goal)?.label ?? answers.goal;
  const interfaceLabel =
    TOOL_FINDER_INTERFACE_OPTIONS.find(o => o.id === answers.interfacePref)?.label ?? answers.interfacePref;

  const catalog = tools
    .map(tool => {
      const group = getToolGroupLabel(tool.group);
      return `- id=${tool.id} | ${tool.label} [${group}]: ${tool.description}`;
    })
    .join('\n');

  const allowedIds = tools.map(t => t.id).join(', ');

  return `IMPORTANT: This is NOT a biomedical / gene-disease question. Do NOT query associations.
Your only job is to recommend DISGENET platform product tools from the catalog below.

Catalog (use ONLY these ids: ${allowedIds}):
${catalog}

User answers collected on the Tools page:
- Goal: ${goalLabel} (${answers.goal})
- Preferred interface: ${interfaceLabel} (${answers.interfacePref})

Reply with ONLY this exact plain-text format (no markdown, no plan, no extra commentary):
TOOL_IDS: id1, id2
SUMMARY: one short sentence
WHY:
- id1: one short reason
- id2: one short reason`;
}

export type ParsedToolFinderRecommendation = {
  toolIds: string[];
  summary: string;
  whyById: Record<string, string>;
};

export function parseToolFinderAnswer(
  answer: string,
  tools: NavToolMenuItem[],
): ParsedToolFinderRecommendation | null {
  const text = answer.trim();
  if (!text) return null;

  const allowed = new Map(tools.map(t => [t.id, t]));

  const idsLine =
    text.match(/^\s*TOOL_IDS:\s*(.+)$/im)?.[1]?.trim() ??
    text
      .match(/\bTOOL_IDS:\s*(.+)/i)?.[1]
      ?.split('\n')[0]
      ?.trim();

  let toolIds: string[] = [];

  if (idsLine) {
    toolIds = idsLine
      .split(/[,|;]/)
      .map(part => resolveToolToken(part, tools))
      .filter((id): id is string => Boolean(id && allowed.has(id)));
  }

  if (toolIds.length === 0) {
    toolIds = extractToolsFromFreeText(text, tools);
  }

  const uniqueIds = [...new Set(toolIds)].slice(0, 2);
  if (uniqueIds.length === 0) return null;

  const summary =
    text.match(/^\s*SUMMARY:\s*(.+)$/im)?.[1]?.trim() ??
    text
      .match(/\bSUMMARY:\s*(.+)/i)?.[1]
      ?.split('\n')[0]
      ?.trim() ??
    text
      .split('\n')
      .map(line => line.trim())
      .find(line => line && !/^(TOOL_IDS|WHY|-)/i.test(line)) ??
    '';

  const whyById: Record<string, string> = {};
  const whyBlock = text.split(/^\s*WHY:\s*/im)[1] ?? '';
  for (const line of whyBlock.split('\n')) {
    const match = line.match(/^\s*[-*]?\s*([^:]+)\s*:\s*(.+)$/);
    if (!match) continue;
    const id = resolveToolToken(match[1], tools);
    const reason = match[2].trim();
    if (id && reason) whyById[id] = reason;
  }

  return { toolIds: uniqueIds, summary, whyById };
}
